class PivotInvoiceForm extends HTMLElement {
  constructor() {
    super();

    // "Solicitar factura" mode: the original single-accordion behaviour,
    // kept available as a simpler alternative to the Boleta/Factura
    // selector (see `display_mode` block setting). Fully self-contained -
    // bails out before touching any selector-mode-only state.
    if (this.dataset.mode === 'simple') {
      this.initSimpleMode();
      return;
    }

    this._touchedRutBoleta = false;
    this._touchedRutFactura = false;
    this._facturaEditing = false;
    this._facturaInitialValues = null;
    this._boletaSavedRut = null; // last value persisted to the cart, or null if never saved
    this._boletaTypeConfirmed = false; // whether TipoDocumento=Boleta has been persisted this session (RUT-disabled config only)
    this._checkoutBusy = false;

    const mappingNode = this.querySelector('#pivot-invoice-regions-mapping');
    this.$regionsMapping = mappingNode ? JSON.parse(mappingNode.textContent) : {};

    this.requireRutBoleta = this.dataset.requireRutBoleta === 'true';
    this.rutBoletaMandatory = this.dataset.rutBoletaMandatory === 'true';

    this.$typeRadios = Array.from(this.querySelectorAll('input[name="pivot-doc-type"]'));
    this.$boletaPanel = this.querySelector('[data-panel="Boleta"]');
    this.$facturaPanel = this.querySelector('[data-panel="Factura"]');

    // Panel Boleta (only rendered when the "Solicitar RUT en boleta" setting is on)
    this.$boletaForm = this.querySelector('#pivot-invoice-boleta-form');
    this.$boletaRutInput = this.querySelector('#InvoiceForm-RUT-Boleta');
    this.$boletaRutError = this.$boletaPanel ? this.$boletaPanel.querySelector('.rut-error-message') : null;

    // Panel Factura
    this.$facturaForm = this.querySelector('#pivot-invoice-form');
    this.$rutInput = this.$facturaForm ? this.$facturaForm.querySelector('#InvoiceForm-RUT') : null;
    this.$regionSelector = this.querySelector('#InvoiceForm-Region');
    this.$communeSelector = this.querySelector('#InvoiceForm-Comuna');
    this.$summary = this.querySelector('.pivot-invoice-summary');
    this.$summaryLabel = this.$summary ? this.$summary.querySelector('.pvt-invoicing-label') : null;
    this.$editButton = this.querySelector('#pivot-invoice-edit');
    this.$clearButton = this.querySelector('#pivot-invoice-clear');
    this.$saveButton = this.querySelector('#pivot-invoice-save');
    this.$facturaRutError = this.$facturaForm ? this.$facturaForm.querySelector('.rut-error-message') : null;

    if (!this.$facturaForm || !this.$rutInput || !this.$regionSelector || !this.$communeSelector) {
      console.warn('PivotInvoiceForm: faltan elementos requeridos en el DOM');
      return;
    }

    // Trust the server-computed flag (same `has_saved_factura` the Liquid
    // uses to decide whether to render the summary or the form) instead of
    // re-scanning the fields here: at this point in the constructor the
    // Comuna <select> has not been populated yet (that only happens in
    // initEvents(), via handleRegionChange()), so a "some field is still
    // empty" check would always read Comuna as blank and wrongly report the
    // Factura data as unsaved on every fresh load.
    this._facturaSaved = this.dataset.hasSavedFactura === 'true';

    // Only treat the RUT as already saved for Boleta if the cart's current
    // document type actually is Boleta with that value persisted - switching
    // the radio to Boleta always requires an explicit save, even if
    // RUTEmpresa happens to hold a value reused from Factura.
    if (this.$boletaRutInput && this.dataset.docType === 'Boleta') {
      this._boletaSavedRut = this.$boletaRutInput.value || null;
    }

    // Resolved once: the checkout button is static SSR markup, it is never
    // replaced by this component, so there is no need to re-resolve it on
    // every interaction like the rest of the reactive state.
    this.$mainButton = this.resolveCheckoutButton();

    this.initEvents();

    // Captured only after initEvents(): that's what actually sets the
    // Comuna <select> to its saved value (handleRegionChange() regenerates
    // its options from scratch, wiping the SSR-rendered `selected` option,
    // then the dataset.value assignment restores it). Snapshotting before
    // that point would freeze Comuna as blank, making isFacturaDirty()
    // permanently see the form as changed and show "Guardar" instead of
    // "Limpiar" even when nothing was actually edited.
    this._facturaInitialValues = this.getFacturaValues();

    this.render();
  }

  /**
   * The checkout button (`#checkout[name="checkout"]`, `form="cart-form"`)
   * is rendered by `snippets/cart-summary.liquid` (main cart page) AND by
   * `snippets/pivot-cart-summary.liquid` (drawer) with the exact same id,
   * so both can be present in the DOM at once (e.g. while browsing /cart
   * with the drawer still mounted). We scope the lookup by ancestor to grab
   * the right one for this instance's context, detected from this element's
   * own DOM position (`cart-drawer-component` ancestor).
   *
   * We deliberately never give our own inputs a `form="cart-form"`
   * attribute to piggyback on that button's native submission: besides the
   * duplicated id above, the Boleta and Factura panels save to two
   * independent forms (`Rut` vs. `RUTEmpresa` and friends). Instead we
   * intercept this button's click, save the current panel's data through
   * our own fetch call, and only then let the (untouched) real submission
   * continue.
   */
  resolveCheckoutButton = () => {
    const buttons = Array.from(document.querySelectorAll('#checkout[name="checkout"]'));
    if (buttons.length === 0) return null;

    const isInDrawer = !!this.closest('cart-drawer-component');
    const scoped = isInDrawer
      ? buttons.find((btn) => btn.closest('cart-drawer-component'))
      : buttons.find((btn) => btn.closest('.cart-page'));

    return scoped || buttons[0];
  };

  get docType() {
    const checked = this.$typeRadios.find((radio) => radio.checked);
    return checked ? checked.value : 'Boleta';
  }

  initEvents = () => {
    this.$typeRadios.forEach((radio) => radio.addEventListener('change', this.handleTypeChange));

    if (this.$boletaForm) {
      // No submit button lives in this form anymore (see class doc comment
      // on resolveCheckoutButton), but it still has a single text field, so
      // the browser's implicit-submission behaviour would otherwise submit
      // it (and reload the page) whenever the shopper presses Enter here.
      this.$boletaForm.addEventListener('submit', (e) => e.preventDefault());
      this.$boletaRutInput.addEventListener('keyup', this.handleBoletaRutChange);
      this.$boletaRutInput.addEventListener('blur', this.handleBoletaRutChange);
    }

    this.$regionSelector.addEventListener('change', this.handleRegionChange);
    this.$rutInput.addEventListener('keyup', this.handleFacturaRutChange);
    this.$rutInput.addEventListener('blur', this.handleFacturaRutChange);
    this.$facturaForm.addEventListener('submit', this.handleFacturaSubmit);
    if (this.$editButton) this.$editButton.addEventListener('click', this.handleEdit);
    if (this.$clearButton) this.$clearButton.addEventListener('click', this.handleFacturaClear);
    if (this.$mainButton) this.$mainButton.addEventListener('click', this.handleCheckoutClick);

    this.handleRegionChange();
    this.$communeSelector.value = this.$communeSelector.dataset.value;

    this.$facturaForm.addEventListener('change', () => this.checkFormState());
    this.$facturaForm.addEventListener('input', () => this.checkFormState());
  };

  render = () => {
    const docType = this.docType;

    if (this.$boletaPanel) this.$boletaPanel.hidden = docType !== 'Boleta';
    if (this.$facturaPanel) this.$facturaPanel.hidden = docType !== 'Factura';

    const showSummary = this._facturaSaved && !this._facturaEditing;
    if (this.$summary) {
      this.$summary.hidden = !showSummary;
      if (showSummary && this.$summaryLabel) {
        this.$summaryLabel.textContent = this.buildFacturaSummaryText();
      }
    }
    this.$facturaForm.hidden = showSummary;

    this.checkFormState();
  };

  buildFacturaSummaryText = () => {
    const razonSocial = this.$facturaForm.querySelector('#InvoiceForm-RazonSocial')?.value || '';
    const rut = this.$rutInput.value || '';
    return `Factura: ${razonSocial} - ${rut}`;
  };

  handleTypeChange = () => {
    const docType = this.docType;
    this.render();

    if (docType === 'Boleta' && !this.requireRutBoleta) {
      this.persistBoletaWithoutRut();
      return;
    }

    // Any other case (Boleta with the RUT field shown, or Factura before
    // it's been filled/saved): only the still-untouched fields need a real
    // save later, but `TipoDocumento` itself must reflect the selector right
    // away. Otherwise `cart.attributes.TipoDocumento` keeps its previous
    // value until checkout, and an unrelated cart re-render in between
    // (e.g. changing another line item's quantity re-renders this whole
    // block from the server) would snap the selector - and the Factura
    // summary with it - back to whatever was last saved.
    this.persistDocTypeOnly(docType);
  };

  /** Keeps `cart.attributes.TipoDocumento` in sync with the selector without touching any other field. */
  persistDocTypeOnly = (docType) => {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ attributes: { TipoDocumento: docType } }),
    }).catch((err) => {
      console.error(err);
    });
  };

  /**
   * Boleta with the "Solicitar RUT en boleta" setting off - there is no
   * `#pivot-invoice-boleta-form` in the DOM at all (see the Liquid `{% if
   * require_rut_boleta %}`), so nothing to read a FormData from. Persists
   * TipoDocumento=Boleta and clears any leftover Factura data directly via a
   * JSON body. Used both when switching to Boleta and, if that never
   * happened yet, right before checkout. Resolves to true on success, false
   * on failure.
   */
  persistBoletaWithoutRut = () => {
    this.setCheckoutBusy(true);
    const attributes = {
      TipoDocumento: 'Boleta',
      Rut: null,
      RUTEmpresa: null,
      RazonSocial: null,
      Giro: null,
      DireccionEmpresa: null,
      Region: null,
      Comuna: null,
    };

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ attributes }),
    })
      .then(() => {
        this._facturaSaved = false;
        this._boletaSavedRut = null;
        this._boletaTypeConfirmed = true;
        this.clearFacturaOnlyFields();
        this._facturaInitialValues = this.getFacturaValues();
        this.render();
        return true;
      })
      .catch((err) => {
        console.error(err);
        return false;
      })
      .finally(() => {
        this.setCheckoutBusy(false);
      });
  };

  setRadiosDisabled = (disabled) => {
    this.$typeRadios.forEach((radio) => {
      radio.disabled = disabled;
    });
  };

  /**
   * Live inline validation feedback, plus the Limpiar/Guardar toggle (same
   * behaviour as the pre-selector version of this block): only one of the
   * two buttons is shown at a time - "Limpiar" once the current values are
   * saved and untouched, "Guardar" otherwise. Does not gate checkout, that
   * is handled by handleCheckoutClick.
   */
  checkFormState = () => {
    this.syncFacturaRutValidity();
    if (this.$boletaForm) this.syncBoletaRutValidity();

    const isValid = this.$facturaForm.checkValidity();
    const showClear = this._facturaSaved && !this.isFacturaDirty() && isValid;

    if (this.$clearButton) this.$clearButton.hidden = !showClear;
    if (this.$saveButton) {
      this.$saveButton.hidden = showClear;
      this.$saveButton.disabled = !isValid || !this.facturaNeedsSave();
    }
  };

  isBoletaValid = () => {
    if (!this.requireRutBoleta) return true;
    const value = this.$boletaRutInput ? this.$boletaRutInput.value : '';
    if (value === '') return !this.rutBoletaMandatory;
    return validateRut(value);
  };

  boletaNeedsSave = () => {
    if (!this.requireRutBoleta) return false; // no RUT field at all - see persistBoletaWithoutRut
    const value = this.$boletaRutInput ? this.$boletaRutInput.value : '';
    return value !== this._boletaSavedRut;
  };

  isFacturaDirty = () => {
    if (!this._facturaInitialValues) return true;
    const current = this.getFacturaValues();
    return Object.keys(this._facturaInitialValues).some((key) => this._facturaInitialValues[key] !== current[key]);
  };

  facturaNeedsSave = () => !this._facturaSaved || this.isFacturaDirty();

  /**
   * Runs right before the real checkout button would navigate away. When
   * the current document type still needs data or has unsaved changes, we
   * block the navigation, persist through our own fetch call, and manually
   * continue the (untouched) native submission once it succeeds - so the
   * shopper never has to press a separate "Guardar" first.
   */
  handleCheckoutClick = (e) => {
    if (this._checkoutBusy) {
      e.preventDefault();
      return;
    }

    const docType = this.docType;

    if (docType === 'Boleta') {
      if (!this.requireRutBoleta) {
        // No RUT field rendered at all - just make sure TipoDocumento was
        // persisted at least once (e.g. the shopper never touched the
        // radio, so `handleTypeChange` never ran).
        if (!this._boletaTypeConfirmed) {
          e.preventDefault();
          this.persistBoletaWithoutRut().then((ok) => {
            if (ok) this.continueToCheckout();
          });
        }
        return;
      }
      if (!this.isBoletaValid()) {
        e.preventDefault();
        this._touchedRutBoleta = true;
        this.syncBoletaRutValidity();
        this.$boletaRutInput.focus();
        return;
      }
      if (this.boletaNeedsSave()) {
        e.preventDefault();
        this.persistBoleta().then((ok) => {
          if (ok) this.continueToCheckout();
        });
      }
      return;
    }

    // Factura
    if (!this.$facturaForm.checkValidity()) {
      e.preventDefault();
      this._touchedRutFactura = true;
      this.syncFacturaRutValidity();
      if (this._facturaSaved && !this._facturaEditing) {
        // The invalid fields are inside the collapsed form - open it back up.
        this._facturaEditing = true;
        this.render();
      }
      this.$facturaForm.reportValidity();
      return;
    }
    if (this.facturaNeedsSave()) {
      e.preventDefault();
      this.persistFactura().then((ok) => {
        if (ok) this.continueToCheckout();
      });
    }
  };

  setCheckoutBusy = (busy) => {
    this._checkoutBusy = busy;
    this.setRadiosDisabled(busy);
    if (!this.$mainButton) return;
    this.$mainButton.style.opacity = busy ? '0.6' : '';
    this.$mainButton.style.cursor = busy ? 'wait' : '';
  };

  continueToCheckout = () => {
    this.setCheckoutBusy(false);
    if (this.$mainButton && this.$mainButton.form) {
      this.$mainButton.form.requestSubmit(this.$mainButton);
    }
  };

  handleRegionChange = () => {
    const region = this.$regionSelector.value;
    const communes = this.$regionsMapping[region];
    if (!communes) return;
    this.$communeSelector.innerHTML =
      '<option value="">Selecciona una comuna</option>' +
      communes.map((commune) => `<option value="${commune}">${commune}</option>`).join('');
  };

  handleBoletaRutChange = () => {
    this._touchedRutBoleta = true;
    this.checkFormState();
  };

  handleFacturaRutChange = () => {
    this._touchedRutFactura = true;
    this.checkFormState();
  };

  syncRutValidity = (input, errorEl, touched) => {
    if (!input || !errorEl) return;
    const value = input.value;
    if (value === '') {
      input.setCustomValidity('');
      errorEl.classList.remove('show');
      errorEl.hidden = true;
      return;
    }

    if (validateRut(value)) {
      input.setCustomValidity('');
      input.value = formatDNI({ rut: value, pais: 'CL' });
      errorEl.classList.remove('show');
      errorEl.hidden = true;
      return;
    }

    input.setCustomValidity('Ingresa un RUT valido');
    errorEl.classList.toggle('show', touched);
    errorEl.hidden = !touched;
  };

  syncBoletaRutValidity = () => {
    this.syncRutValidity(this.$boletaRutInput, this.$boletaRutError, this._touchedRutBoleta);
  };

  syncFacturaRutValidity = () => {
    this.syncRutValidity(this.$rutInput, this.$facturaRutError, this._touchedRutFactura);
  };

  getFacturaValues = () => {
    const values = {};
    this.$facturaForm.querySelectorAll('input, select').forEach((input) => {
      values[input.name] = input.value;
    });
    return values;
  };

  /**
   * Clears every Factura field in the (still mounted) Factura form after
   * saving Boleta clears those same attributes server-side (Rut and
   * RUTEmpresa are independent attributes since each document type has its
   * own field). Keeps the form from showing stale values that no longer
   * exist on the cart.
   */
  clearFacturaOnlyFields = () => {
    this.$facturaForm.querySelectorAll('input, select').forEach((input) => {
      input.value = '';
    });
    this.handleRegionChange();
  };

  /** POSTs the Boleta RUT to the cart. Resolves to true on success, false on failure. */
  persistBoleta = () => {
    this.setCheckoutBusy(true);

    const formData = new FormData(this.$boletaForm);
    formData.set('attributes[TipoDocumento]', 'Boleta');
    // Clear any leftover Factura-only data so `cart.attributes` only ever
    // holds the fields for the currently selected document type.
    formData.set('attributes[RUTEmpresa]', '');
    formData.set('attributes[RazonSocial]', '');
    formData.set('attributes[Giro]', '');
    formData.set('attributes[DireccionEmpresa]', '');
    formData.set('attributes[Region]', '');
    formData.set('attributes[Comuna]', '');

    const value = this.$boletaRutInput.value;

    return fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        this._boletaSavedRut = value;
        this._facturaSaved = false;
        this.clearFacturaOnlyFields();
        this._facturaInitialValues = this.getFacturaValues();
        return true;
      })
      .catch((err) => {
        console.error(err);
        return false;
      })
      .finally(() => {
        // Also covers the checkout path: `continueToCheckout()` clears busy
        // again on success, which is harmless.
        this.setCheckoutBusy(false);
      });
  };

  /** "Guardar" button in the Factura panel - saves without leaving the cart. */
  handleFacturaSubmit = (e) => {
    e.preventDefault();

    if (!this.$facturaForm.checkValidity()) {
      this._touchedRutFactura = true;
      this.syncFacturaRutValidity();
      this.$facturaForm.reportValidity();
      return;
    }

    const label = this.$saveButton ? this.$saveButton.querySelector('.button-text') : null;
    const originalText = label ? label.textContent : '';
    if (this.$saveButton) this.$saveButton.disabled = true;
    if (label) label.textContent = 'Guardando...';

    this.persistFactura().finally(() => {
      if (label) label.textContent = originalText;
    });
  };

  /** POSTs the full Factura form to the cart. Resolves to true on success, false on failure. */
  persistFactura = () => {
    this.setCheckoutBusy(true);

    const formData = new FormData(this.$facturaForm);
    formData.set('attributes[TipoDocumento]', 'Factura');
    // Clear the Boleta-only RUT so `cart.attributes` only ever holds the
    // fields for the currently selected document type.
    formData.set('attributes[Rut]', '');

    return fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        this._facturaSaved = true;
        this._facturaEditing = false;
        this._facturaInitialValues = this.getFacturaValues();
        this.render();
        return true;
      })
      .catch((err) => {
        console.error(err);
        return false;
      })
      .finally(() => {
        // Also covers the checkout path: `continueToCheckout()` clears busy
        // again on success, which is harmless. Without this, saving via the
        // "Guardar" button (which never calls continueToCheckout) would
        // leave the checkout button disabled/dimmed forever.
        this.setCheckoutBusy(false);
      });
  };

  handleEdit = () => {
    this._facturaEditing = true;
    this.render();
  };

  /** "Limpiar" button - erases the saved Factura data entirely (same behaviour as the pre-selector version). */
  handleFacturaClear = () => {
    const label = this.$clearButton.querySelector('.button-text');
    const originalText = label ? label.textContent : '';
    this.$clearButton.disabled = true;
    if (label) label.textContent = 'Limpiando...';
    this.setCheckoutBusy(true);

    const attributes = {
      RUTEmpresa: null,
      RazonSocial: null,
      Giro: null,
      DireccionEmpresa: null,
      Region: null,
      Comuna: null,
    };

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ attributes }),
    })
      .then(() => {
        this.$facturaForm.querySelectorAll('input, select').forEach((input) => {
          input.value = '';
        });
        this.handleRegionChange();
        this._touchedRutFactura = false;
        if (this.$facturaRutError) {
          this.$facturaRutError.classList.remove('show');
          this.$facturaRutError.hidden = true;
        }
        this._facturaSaved = false;
        this._facturaEditing = false;
        this._facturaInitialValues = this.getFacturaValues();
        this.render();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.setCheckoutBusy(false);
        this.$clearButton.disabled = false;
        if (label) label.textContent = originalText;
      });
  };

  // ---------------------------------------------------------------------
  // "Solicitar factura" (simple) mode
  // ---------------------------------------------------------------------

  initSimpleMode = () => {
    this._simpleState = {
      button: 'save',
      disabledButton: true,
      disabledMainButton: false,
    };
    this._simpleTouchedRut = false;
    this._simpleInitialValues = null;
    this._simpleHasSavedData = false;
    this.$simpleMainButton = null;

    const mappingNode = this.querySelector('#pivot-invoice-regions-mapping');
    this.$simpleRegionsMapping = mappingNode ? JSON.parse(mappingNode.textContent) : {};

    this.$simpleRutInput = this.querySelector('#InvoiceForm-Simple-RUT');
    this.$simpleRegionSelector = this.querySelector('#InvoiceForm-Simple-Region');
    this.$simpleCommuneSelector = this.querySelector('#InvoiceForm-Simple-Comuna');
    this.$simpleForm = this.querySelector('#pivot-invoice-simple-form');
    this.$simpleLabel = this.querySelector('.cart-discount__label');
    this.$simpleButton = this.querySelector('#pivot-invoice-simple-save');
    this.$simpleClearButton = this.querySelector('#pivot-invoice-simple-clear');
    this.$simpleRutError = this.querySelector('.rut-error-message');

    if (!this.$simpleForm || !this.$simpleRutInput || !this.$simpleRegionSelector || !this.$simpleCommuneSelector) {
      console.warn('PivotInvoiceForm (simple): faltan elementos requeridos en el DOM');
      return;
    }

    this.initSimpleEvents();

    this._simpleInitialValues = this.getSimpleFormValues();
    this._simpleHasSavedData = this.areSimpleFieldsFilled();

    this.checkSimpleFormState(false);
  };

  initSimpleEvents = () => {
    this.$simpleRegionSelector.addEventListener('change', this.handleSimpleRegionChange);
    this.$simpleRutInput.addEventListener('keyup', this.handleSimpleRutChange);
    this.$simpleRutInput.addEventListener('blur', this.handleSimpleRutChange);
    this.$simpleForm.addEventListener('submit', this.handleSimpleSubmit);
    this.$simpleClearButton.addEventListener('click', this.handleSimpleClear);

    this.handleSimpleRegionChange();
    this.$simpleCommuneSelector.value = this.$simpleCommuneSelector.dataset.value;
    this.$simpleRutError.classList.remove('show');
    this.$simpleRutError.hidden = true;

    this.$simpleForm.addEventListener('change', () => this.checkSimpleFormState());
    this.$simpleForm.addEventListener('input', () => this.checkSimpleFormState());
  };

  onSimpleStateChange = () => {
    this.$simpleMainButton = this.resolveCheckoutButton();

    this.$simpleButton.disabled = this._simpleState.disabledButton;
    this.$simpleClearButton.disabled = this._simpleState.disabledButton;

    if (this.$simpleMainButton) {
      const isCheckoutDisabled = this._simpleState.disabledMainButton;
      this.$simpleMainButton.disabled = isCheckoutDisabled;
      this.$simpleMainButton.style.opacity = isCheckoutDisabled ? '0.6' : '';
      this.$simpleMainButton.style.cursor = isCheckoutDisabled ? 'not-allowed' : '';
      this.$simpleMainButton.style.pointerEvents = isCheckoutDisabled ? 'none' : '';
    }

    if (this._simpleState.button === 'save') {
      this.$simpleButton.hidden = false;
      this.$simpleClearButton.hidden = true;
    } else {
      this.$simpleButton.hidden = true;
      this.$simpleClearButton.hidden = false;
    }
  };

  checkSimpleFormState = () => {
    this.syncSimpleRutValidity();

    const $invalid = this.$simpleForm.querySelector(':invalid');
    const $inputs = this.$simpleForm.querySelectorAll('input, select');
    const allFilled = Array.from($inputs).every((input) => input.value !== '');
    const isDirty = this.isSimpleDirty();

    if (!isDirty && this._simpleHasSavedData && allFilled && !$invalid) {
      this._simpleState.button = 'clear';
      this._simpleState.disabledButton = false;
    } else {
      this._simpleState.button = 'save';
      this._simpleState.disabledButton = !allFilled || !!$invalid;
    }

    this._simpleState.disabledMainButton = isDirty;
    this.onSimpleStateChange();
  };

  handleSimpleRegionChange = () => {
    const region = this.$simpleRegionSelector.value;
    const communes = this.$simpleRegionsMapping[region];
    if (!communes) return;
    this.$simpleCommuneSelector.innerHTML =
      '<option value="">Selecciona una comuna</option>' +
      communes.map((commune) => `<option value="${commune}">${commune}</option>`).join('');
  };

  handleSimpleRutChange = () => {
    this._simpleTouchedRut = true;
    this.syncSimpleRutValidity();
    this.checkSimpleFormState();
  };

  syncSimpleRutValidity = () => {
    const value = this.$simpleRutInput.value;
    if (value === '') {
      this.$simpleRutInput.setCustomValidity('');
      this.$simpleRutError.classList.remove('show');
      this.$simpleRutError.hidden = true;
      return;
    }

    if (validateRut(value)) {
      this.$simpleRutInput.setCustomValidity('');
      this.$simpleRutInput.value = formatDNI({ rut: value, pais: 'CL' });
      this.$simpleRutError.classList.remove('show');
      this.$simpleRutError.hidden = true;
      return;
    }

    this.$simpleRutInput.setCustomValidity('Ingresa un RUT valido');
    const shouldShow = this._simpleTouchedRut === true;
    this.$simpleRutError.classList.toggle('show', shouldShow);
    this.$simpleRutError.hidden = !shouldShow;
  };

  getSimpleFormValues = () => {
    const values = {};
    this.$simpleForm.querySelectorAll('input, select').forEach((input) => {
      values[input.name] = input.value;
    });
    return values;
  };

  areSimpleFieldsFilled = () => Array.from(this.$simpleForm.querySelectorAll('input, select')).every((input) => input.value !== '');

  isSimpleDirty = () => {
    if (!this._simpleInitialValues) return false;
    const current = this.getSimpleFormValues();
    return Object.keys(this._simpleInitialValues).some((key) => this._simpleInitialValues[key] !== current[key]);
  };

  handleSimpleSubmit = (e) => {
    e.preventDefault();

    if (this.$simpleRutInput.value && !validateRut(this.$simpleRutInput.value)) {
      this._simpleTouchedRut = true;
      this.syncSimpleRutValidity();
      return;
    }

    const formData = new FormData(this.$simpleForm);
    const data = Object.fromEntries(formData);
    this.$simpleButton.disabled = true;
    this.$simpleButton.querySelector('.button-text').textContent = 'Guardando...';
    this.$simpleClearButton.disabled = true;
    this._simpleState.disabledButton = true;
    this._simpleState.disabledMainButton = true;
    this.onSimpleStateChange();

    fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        if (data['attributes[RUTEmpresa]'] && data['attributes[RazonSocial]']) {
          this.$simpleLabel.textContent = `Factura: ${data['attributes[RazonSocial]']} - ${data['attributes[RUTEmpresa]']}`;
        } else {
          this.$simpleLabel.textContent = '';
        }

        this._simpleInitialValues = this.getSimpleFormValues();
        this._simpleHasSavedData = this.areSimpleFieldsFilled();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.$simpleButton.disabled = false;
        this.$simpleButton.querySelector('.button-text').textContent = 'Guardar';
        this.$simpleClearButton.disabled = false;
        this.checkSimpleFormState();
      });
  };

  handleSimpleClear = () => {
    const attributes = {
      RUTEmpresa: null,
      RazonSocial: null,
      Giro: null,
      DireccionEmpresa: null,
      Region: null,
      Comuna: null,
    };
    this.$simpleRutError.classList.remove('show');
    this.$simpleClearButton.disabled = true;
    this.$simpleClearButton.querySelector('.button-text').textContent = 'Limpiando...';
    this.$simpleButton.disabled = true;
    this._simpleState.disabledButton = true;
    this._simpleState.disabledMainButton = true;
    this.onSimpleStateChange();

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ attributes }),
    })
      .then(() => {
        this.$simpleForm.querySelectorAll('input, select').forEach((el) => (el.value = ''));
        this.$simpleLabel.textContent = '¿Necesitas factura?';
        this._simpleTouchedRut = false;
        this.$simpleRutError.hidden = true;
        this._simpleInitialValues = this.getSimpleFormValues();
        this._simpleHasSavedData = false;
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.$simpleButton.disabled = false;
        this.$simpleButton.querySelector('.button-text').textContent = 'Guardar';
        this.$simpleClearButton.disabled = false;
        this.$simpleClearButton.querySelector('.button-text').textContent = 'Limpiar';
        this.checkSimpleFormState();
      });
  };
}

if (!customElements.get('pivot-invoice-form')) {
  customElements.define('pivot-invoice-form', PivotInvoiceForm);
}

const byCountry = {
  cl: function ({ rut }) {
    const regex = /[^\dkK]/g;

    rut = rut.toString().replace(regex, '');
    const dv = rut.slice(-1).toUpperCase();
    if (rut && dv) {
      rut = rut.slice(0, -1) + '-' + dv;
    }
    return rut;
  },
};

function formatDNI({ rut, pais, tipo }) {
  return byCountry[pais?.toLowerCase()]?.({ rut, tipo }) || rut;
}

function validateRut(rut) {
  rut = rut.replace(/[^0-9kK]/g, '');
  if (rut.length < 8 || rut.length > 9) {
    return false;
  }
  let cuerpo = rut.slice(0, -1);
  let dv = rut.slice(-1).toUpperCase();

  let suma = cuerpo
    .split('')
    .reverse()
    .reduce((acc, digit, index) => acc + parseInt(digit) * ((index % 6) + 2), 0);

  let resultado = 11 - (suma % 11);
  let digitoVerificador = resultado === 11 ? '0' : resultado === 10 ? 'K' : resultado.toString();
  return dv == digitoVerificador;
}
